import React, { useCallback } from 'react';
import cx from 'classnames';
import { faColon, faFilter, faLock } from '@fortawesome/pro-solid-svg-icons';
import {
  faChevronsDown,
  faChevronsUp,
} from '@fortawesome/pro-regular-svg-icons';
import { Card, CardBody, Button } from 'reactstrap';
import { Icon } from 'js/components';
import OrgNodePicker from 'js/components/OrgNodePicker/OrgNodePicker';
import OrgTreeNodeTypeIcon from 'js/routes/Organizations/components/ManageOrg/OrgTreeVeiw/OrgTreeNodeTypeIcon';
import {
  orgTrendLineKindLabel,
  orgTrendLineKindPaletteClass,
} from 'js/utils/org-trends-rollup';
import { useOrgTrends } from './OrgTrendsContext';

import pickerStyles from 'js/components/OrgNodePicker/OrgNodePicker.module.scss';
import styles from './OrgTrendsHeader.module.scss';

function OrgTrendsPickerToggle({
  selectedRow,
  fallbackLabel,
  toggleButtonProps,
  isOpen,
}) {
  return (
    <button
      type="button"
      className={styles['filter-toggle']}
      aria-label="Filter teams by org structure"
      {...toggleButtonProps}
    >
      {selectedRow ? (
        <OrgTreeNodeTypeIcon
          node={selectedRow}
          className={styles['filter-icon']}
        />
      ) : null}
      <span className={styles['filter-label']}>
        {selectedRow?.name ?? fallbackLabel ?? 'Organization'}
      </span>
      <Icon
        icon={['far', isOpen ? 'chevron-up' : 'chevron-down']}
        fixedWidth
        className={styles['filter-caret']}
      />
    </button>
  );
}

function OrgTrendsHeader({ showQuestionSetSections = false }) {
  const {
    activeLine,
    lockedLine,
    clearLockedLine,
    expandAll,
    collapseAll,
    allExpanded,
    hasQuestionSets,
    organization,
    pickerRows,
    selectedPickerRow,
    setSelectedPickerRow,
  } = useOrgTrends();

  const PickerToggle = useCallback(
    (props) => (
      <OrgTrendsPickerToggle {...props} fallbackLabel={organization?.name} />
    ),
    [organization?.name]
  );

  const showExpandToggle = showQuestionSetSections && hasQuestionSets;
  const kindPaletteClass = activeLine
    ? orgTrendLineKindPaletteClass({
        isRollup: activeLine.isRollup,
        depth: activeLine.depth ?? 0,
      })
    : null;
  const kindLabel = activeLine
    ? orgTrendLineKindLabel({
        isRollup: activeLine.isRollup,
        depth: activeLine.depth ?? 0,
      })
    : null;

  return (
    <Card className={styles.header}>
      <CardBody>
        <div className={cx('manage-org', styles.filter)}>
          <span className="text-info">
            <Icon icon={faFilter} className="icon" />
            <Icon icon={faColon} className="icon" transform="shrink-4" />
          </span>
          <OrgNodePicker
            rows={pickerRows}
            selectedRow={selectedPickerRow}
            onSelect={setSelectedPickerRow}
            disabled={!pickerRows.length}
            className={pickerStyles.compact}
            toggleComponent={PickerToggle}
          />
        </div>
        <div className={styles.selection}>
          {activeLine ? (
            <>
              {lockedLine && (
                <Button
                  type="button"
                  outline
                  color="warning"
                  onClick={clearLockedLine}
                  aria-label="Unlock selected line"
                  title="Unlock selected line"
                >
                  <Icon icon={faLock} className="icon" fixedWidth />
                </Button>
              )}
              <span className={styles.selectionName}>{activeLine.label}</span>
              <span
                className={cx(
                  'text-only',
                  kindPaletteClass,
                  styles.selectionKind
                )}
              >
                <Icon
                  icon={activeLine.isRollup ? 'sitemap' : 'users'}
                  fixedWidth
                  className={cx('icon-left', styles.selectionKindIcon)}
                />
                {kindLabel}
              </span>
            </>
          ) : (
            <span className={styles.selectionEmpty}>{'\u00a0'}</span>
          )}
        </div>
        <div className={styles.controls}>
          {showExpandToggle && (
            <Button
              type="button"
              outline
              color="info"
              onClick={allExpanded ? collapseAll : expandAll}
              size="sm"
              aria-label={
                allExpanded ? 'Hide all questions' : 'Show all questions'
              }
              title={allExpanded ? 'Hide all questions' : 'Show all questions'}
            >
              <Icon
                icon={allExpanded ? faChevronsDown : faChevronsUp}
                className="icon"
                fixedWidth
              />
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export default OrgTrendsHeader;
